// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { TerceraChartCashItemSeriesDataType } from "../Series/TerceraChartCashItemSeriesEnums.js";
import { DataCacheToolRayType } from "../../Commons/cache/DataCacheToolEnums.js";
import { ToolView } from "./ToolView.js";
import { ToolViewUtils } from "./Utils/ToolViewUtils.js";

export let FibonacciExpansionToolView = function (tool)
{
    ToolView.call(this, tool);
};

FibonacciExpansionToolView.prototype = Object.create(ToolView.prototype);

FibonacciExpansionToolView.prototype.DrawFib = function (gr, ww, highlight)
{
    var pc = ww.PointsConverter;
    var clientRect = ww.ClientRectangle;

    var dataCacheTool = this.dataCacheTool;

    var fibLevel = dataCacheTool.fibLevel;
    var points = dataCacheTool.Points;
    
    var ray = dataCacheTool.RayType;
    var pen = highlight ? dataCacheTool.PenHighlight : dataCacheTool.Pen;
    var penRay = dataCacheTool.PenRay;
    var font = dataCacheTool.font;
    var fontColor = dataCacheTool.FontColor;

    var instrument = dataCacheTool.Instrument;

    var screenPoints = this.screenPoints;

    var scrP0  = screenPoints[0];
    var scrP0X = scrP0[0];
    var scrP0Y = scrP0[1];

    var scrP1  = screenPoints[1];
    var scrP1X = scrP1[0];
    var scrP1Y = scrP1[1];

    var scrP2  = screenPoints[2];
    var scrP2X = scrP2[0];
    var scrP2Y = scrP2[1];

    var p0 = points[0];
    var p0X = p0[0];
    var p0Y = p0[1];

    var p1 = points[1];
    var p1X = p1[0];
    var p1Y = p1[1];

    var p2 = points[2];
    var p2X = p2[0];
    var p2Y = p2[1];

    var yDelta = p2Y - p1Y;

    var fibLevel_len = fibLevel.length;

    var levelsY = new Array(fibLevel_len);

    for (var i = 0; i < fibLevel_len; i++)
    {
        var fibLvl = fibLevel[i];

        // если уровень 0% - считать не надо ибо при делении на 0 => infinity
        if (fibLvl === 0) levelsY[i] = p2Y;
        else
        {
            var lambda = (fibLvl / 100 + 1) / (fibLvl / -100);
            levelsY[i] = (p0Y + lambda * p1Y) / (1 + lambda) + yDelta;
        }
    }

    var dY = p1Y - p0Y;
    ToolView.DrawLineWithRay(gr, ww, scrP0X, scrP0Y, scrP1X, scrP1Y, DataCacheToolRayType.NoRay, pen);
    ToolView.DrawLineWithRay(gr, ww, scrP1X, scrP1Y, scrP2X, scrP2Y, DataCacheToolRayType.NoRay, pen);

    var dX =  Math.abs(scrP2X - scrP1X);
    var minScrPX = Math.min(scrP0X, scrP1X);
    var right = Math.min(dX * 5 + minScrPX, clientRect.X + clientRect.Width);

    var dataType = ww.IsMainWindow && ww.ToolsRenderer.chart.cashItemSeriesSettings ?
        ww.ToolsRenderer.chart.cashItemSeriesSettings.DataType :
        TerceraChartCashItemSeriesDataType.Absolute;

    for (var i = 0; i < fibLevel_len; i++)
    {
        var fibLvl = fibLevel[i];
        var cY = levelsY[i];

        var cYdraw = 0;
        switch (dataType)
        {
            case TerceraChartCashItemSeriesDataType.Absolute:
                cYdraw = pc.GetScreenY(cY);
                break;
            case TerceraChartCashItemSeriesDataType.Relative:
                cYdraw = pc.GetScreenY(ww.ToolsRenderer.chart.cashItemSeriesSettings.relativeDataConverter.Calculate(cY));
                break;
            case TerceraChartCashItemSeriesDataType.Log:
                cYdraw = pc.GetScreenY(ww.ToolsRenderer.chart.cashItemSeriesSettings.logDataConverter.Calculate(cY));
                break;
        }

        if (cYdraw > clientRect.Y && cYdraw < clientRect.Y + clientRect.Height)
            ToolView.DrawLineWithRay(gr, ww, minScrPX, cYdraw, right, cYdraw, ray, pen, penRay);

        //#18801. Drawings - мелкие доработки - показываем  цену каждого уровня слева
        var minPoint = minScrPX;
        minPoint = minPoint > clientRect.X ? minPoint : (clientRect.X < right ? clientRect.X : right);
        // To prevent text-on-text overlay.
        if (!highlight && scrP0Y !== scrP1Y)
        {
            var price = ToolViewUtils.FormatPrice(instrument, cY);
            //#25534  FIbonacci retracement & expansions - подписи для линий скрывать когда данные тулзы уходят из поля зрения, т.е когда двигаем чарт в право
            if (minPoint < clientRect.X + clientRect.Width)
            {
                var f_txt = ToolViewUtils.FormatFibonacciText(fibLvl, points, instrument) + "% (" + price + ")";
                var wid = gr.GetTextWidth(f_txt, font);

                if (ray === DataCacheToolRayType.LeftRay && dX * 5 + minScrPX > clientRect.X + clientRect.Width)
                    ToolViewUtils.DrawFibonacciText(gr, ww, 1, cYdraw, f_txt, font, fontColor);
                else
                    ToolViewUtils.DrawFibonacciText(gr, ww, right - wid, cYdraw, f_txt, font, fontColor);
            }
        }
    }
};

FibonacciExpansionToolView.prototype.Draw = function (gr, ww, param)
{
    this.DrawFib(gr, ww, false);
    ToolView.prototype.Draw.call(this, gr, ww, param);
};

FibonacciExpansionToolView.prototype.DrawSelection = function (gr, ww)
{
    this.DrawFib(gr, ww, true);

    var screenPoints = this.screenPoints;

    var p0 = screenPoints[0];
    var x0 = p0[0];
    var y0 = p0[1];

    var p1 = screenPoints[1];
    var x1 = p1[0];
    var y1 = p1[1];

    var p2 = screenPoints[2];
    var x2 = p2[0];
    var y2 = p2[1];

    this.DrawSelectedPoint(gr, ww, x0, y0);
    this.DrawSelectedPoint(gr, ww, x1, y1);
    this.DrawSelectedPoint(gr, ww, x2, y2);

    this.DrawSelectedLine(gr, ww, x0, y0, x1, y1);
    this.DrawSelectedLine(gr, ww, x1, y1, x2, y2);
};